export interface LatLng {
  lat: number;
  lng: number;
}

const EARTH_RADIUS_M = 6_371_000;

/**
 * Distância em linha reta, em metros. Usada só para o filtro de raio do
 * matching e para ordenar candidatos — a distância que vira preço vem da
 * Google Directions.
 */
export function haversineMeters(a: LatLng, b: LatLng): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);

  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;

  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Raio de tolerância do ponto de embarque, em metros.
 *
 * Tem que caber o erro do GPS do celular (dezenas de metros) e o erro do
 * passageiro marcando o ponto no mapa, sem caber uma corrida inteira:
 * Aguanil é pequena, e uma viagem típica aqui tem menos de 1 km. Com 300
 * m — o primeiro valor — o motorista marcava "cheguei" do outro lado do
 * bairro e a guarda deixava passar.
 *
 * Vale para os dois lados: é o mesmo número que trava o "cheguei" do
 * motorista e que dispara o alerta de afastamento do passageiro. Se os
 * dois discordassem, um veria "chegou" enquanto o outro ainda é avisado
 * para voltar ao ponto.
 *
 * ponytail: número fixo, calibrado no olho. A API registra a distância
 * medida em cada etapa de embarque — ajustar por esse log, não por
 * palpite.
 */
export const PICKUP_RADIUS_M = 150;

/** Se o motorista está perto o bastante do embarque para dizer que chegou. */
export function isAtPickup(driver: LatLng, origin: LatLng): boolean {
  return haversineMeters(driver, origin) <= PICKUP_RADIUS_M;
}

export function isValidLatLng(p: LatLng): boolean {
  return (
    Number.isFinite(p.lat) &&
    Number.isFinite(p.lng) &&
    p.lat >= -90 &&
    p.lat <= 90 &&
    p.lng >= -180 &&
    p.lng <= 180
  );
}

/**
 * Área atendida: Aguanil e Campo Belo, MG.
 *
 * É o mesmo recorte carregado no roteador e nos tiles. Fora daqui não há
 * mapa desenhado nem rota calculável, então o app centraliza aqui quando
 * ainda não sabe onde o usuário está.
 */
export const SERVICE_AREA = {
  center: { lat: -20.92, lng: -45.335 },
  bounds: { minLat: -21.25, maxLat: -20.6, minLng: -45.75, maxLng: -44.95 },
  cities: [
    { name: 'Aguanil', lat: -20.9425, lng: -45.3927 },
    { name: 'Campo Belo', lat: -20.8975, lng: -45.2772 },
  ],
} as const;

export function isInServiceArea(p: LatLng): boolean {
  const b = SERVICE_AREA.bounds;
  return p.lat >= b.minLat && p.lat <= b.maxLat && p.lng >= b.minLng && p.lng <= b.maxLng;
}
